import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type {
  Category,
  CategoryState,
} from "../../../redux/types/categoryTypes";
import { ChoiceField, SellingField, StepHeading } from "./FormFields";
import { genderLabels, SellingValues } from "./types";
import { validateField } from "./validation";

export default function ToyDetailsStep({
  categories,
  loading,
  error,
  retry,
}: {
  categories: Category[];
  loading: CategoryState["loading"];
  error: CategoryState["error"];
  retry: () => void;
}) {
  const { control, setValue, getValues } = useFormContext<SellingValues>();
  const [title, description, categoryId] = useWatch({
    control,
    name: ["title", "description", "categoryId"],
  });
  const subcategories =
    categories.find((item) => item.id === categoryId)?.subcategories || [];
  return (
    <>
      <StepHeading title="Tell Us About Your Toy">
        A great title and the right category help your toy find its next owner.
        Required fields are marked *.
      </StepHeading>
      <Stack spacing={3}>
        <Box>
          <SellingField
            name="title"
            label="Product title"
            required
            maxLength={60}
            placeholder="LEGO Star Wars Millennium Falcon"
          />
          <Typography
            variant="caption"
            component="p"
            textAlign="right"
            sx={{ mt: 0.5 }}
          >
            {title.length}/60 characters
          </Typography>
        </Box>
        <Box>
          <SellingField
            name="description"
            label="Description (optional)"
            maxLength={300}
            multiline
            helper="Tell buyers about accessories, original packaging, missing pieces, wear, or collectible details."
          />
          <Typography
            variant="caption"
            component="p"
            textAlign="right"
            sx={{ mt: 0.5 }}
          >
            {description.length}/300 characters
          </Typography>
        </Box>
        <ChoiceField
          name="gender"
          label="Gender"
          options={Object.entries(genderLabels).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <Box>
          <Typography fontWeight={650} color="secondary.main" sx={{ mb: 2 }}>
            What Kind of Toy Is It?
          </Typography>
          {loading && (
            <Stack direction="row" spacing={1} role="status" sx={{ mb: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading toy categories...</Typography>
            </Stack>
          )}
          {!loading && (error || !categories.length) && (
            <Alert
              severity={error ? "error" : "info"}
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" onClick={retry}>
                  Retry
                </Button>
              }
            >
              {error ||
                "No toy categories are available yet. Try loading them again."}
            </Alert>
          )}
          <Grid container spacing={2}>
            {(["categoryId", "subcategoryId"] as const).map((name) => (
              <Grid item xs={12} sm={6} key={name}>
                <Controller
                  name={name}
                  control={control}
                  rules={{
                    validate: () =>
                      validateField(name, getValues(), categories),
                  }}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      id={`sell-${name}`}
                      select
                      fullWidth
                      required
                      label={
                        name === "categoryId" ? "Main category" : "Subcategory"
                      }
                      value={
                        (name === "categoryId"
                          ? categories
                          : subcategories
                        ).some((item) => item.id === field.value)
                          ? field.value
                          : ""
                      }
                      disabled={
                        loading ||
                        !!error ||
                        (name === "categoryId"
                          ? !categories.length
                          : !categoryId || !subcategories.length)
                      }
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        (name === "subcategoryId"
                          ? !categoryId
                            ? "Choose a main category first."
                            : !subcategories.length
                              ? "This category has no subcategories available."
                              : "Choose the closest match for your toy."
                          : undefined)
                      }
                      onChange={(event) => {
                        field.onChange(event);
                        if (name === "categoryId")
                          setValue("subcategoryId", "", { shouldDirty: true });
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 3 },
                        "& .MuiSelect-select": {
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Choose a category
                      </MenuItem>
                      {(name === "categoryId" ? categories : subcategories).map(
                        (item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                            sx={{ whiteSpace: "normal" }}
                          >
                            {item.name}
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  )}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px !important",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRounded />}>
            <Typography fontWeight={600}>
              Product identifiers{" "}
              <Typography component="span" color="text.secondary">
                (optional)
              </Typography>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SellingField
                  name="upc"
                  label="UPC"
                  maxLength={128}
                  helper="Keep any leading zeros."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SellingField
                  name="gtin"
                  label="GTIN / Other identifier"
                  maxLength={128}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </>
  );
}
